import React, { useRef, useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";
import { WisgptContextTokenQueryVariables } from "@/shared/services/api/graphql/generated";
import { ExamplePromptsSelect } from "./ExamplePromptsSelect";
import { ChatTextInput } from "./ChatTextInput";
import { ChatMessage, ChatMessageComponentProps } from "./ChatMessage";
import {
  ChatMessage as ChatMessageType,
  ToolCall,
  ToolCallResponse,
} from "@/shared/contexts/gptgateway.context";
import { MessagesList } from "./MessagesList";
import { ToolCallLoader } from "./ToolCallLoader";
import {
  emitGptPromptEvent,
  useGptGatewayChat,
} from "@/shared/hooks/gpt/useGptGatewayChat";
import { CustomTagsData } from "@/shared/widgets/MarkdownText/_components/MarkdownComponents";

export interface GatewayChatProps {
  scope: WisgptContextTokenQueryVariables;
  flowId: string;
  examplePrompts?: string[];
  chatTitle?: string;
  isReady?: boolean;
  focusedContext?: {
    id: string;
    prompt?: string;
    params?: Record<string, any>;
    message?: ChatMessageComponentProps;
  };
  messageTagsData?: CustomTagsData;
}
export interface FormInputs {
  message: string;
}

const GatewayChat: React.FC<GatewayChatProps> = (props) => {
  const {
    flowId,
    scope,
    examplePrompts,
    chatTitle,
    focusedContext = { id: "default" },
    messageTagsData,
    isReady = false,
  } = props;

  const { register, handleSubmit, reset } = useForm<FormInputs>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const { flowStatus, messages, stream } = useGptGatewayChat({
    flowId,
    isReady,
    scope,
    focusedContext,
  });

  // flowStatus is already conversation-specific from useGptGatewayChat
  const currentFlowStatus = flowStatus;

  const bottomOffset =
    !!window?.document?.getElementsByClassName("intercom-launcher");

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const renderChatMessage = (message: ChatMessageType) => (
    <ChatMessage {...message} messageTagsData={messageTagsData} />
  );

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    if (currentFlowStatus == "idle") {
      emitGptPromptEvent(
        flowId,
        focusedContext.id
      )({
        prompt: data.message,
        params: focusedContext?.params,
        message: {
          role: "user",
          content: data.message,
        },
      });
      reset();
    }
  };

  const handleSelectExamplePrompt = (prompt: string) => {
    emitGptPromptEvent(
      flowId,
      focusedContext.id
    )({
      prompt,
      params: focusedContext?.params,
      message: {
        role: "user",
        content: prompt,
      },
    });
  };

  // Filter to only show user and assistant messages
  const filteredMessages =
    messages[focusedContext.id]?.filter(
      (message) => message.role === "user" || message.role === "assistant"
    ) || [];

  const chatMessages =
    stream?.length > 0
      ? ([
          ...filteredMessages,
          {
            role: "assistant",
            content: stream.join(""),
            isStream: true,
          },
        ] as ChatMessageType[])
      : filteredMessages;

  // Get the latest tool call for loading state (only when not streaming)
  const getLatestToolCall = () => {
    const allMessages = messages[focusedContext.id] || [];
    // Don't show tool call loader if we're already streaming the response
    if (
      allMessages.length === 0 ||
      currentFlowStatus !== "loading" ||
      stream?.length > 0
    )
      return null;

    // Find the last assistant message index
    let lastAssistantIndex = -1;
    for (let i = allMessages.length - 1; i >= 0; i--) {
      if (allMessages[i].role === "assistant") {
        lastAssistantIndex = i;
        break;
      }
    }

    // Get tool calls after the last assistant message
    const toolCallsAfterAssistant = allMessages.slice(lastAssistantIndex + 1);
    const toolCalls = toolCallsAfterAssistant.filter(
      (msg) => msg.role === "tool_call" || msg.role === "tool_call_response"
    );

    // Return the latest tool call
    if (toolCalls.length > 0) {
      const latest = toolCalls[toolCalls.length - 1];
      return latest.content as ToolCall | ToolCallResponse;
    }

    return null;
  };

  const latestToolCall = getLatestToolCall();

  // Show thinking state when loading but no tool call and not streaming
  const isThinking =
    currentFlowStatus === "loading" && !latestToolCall && stream?.length === 0;

  return (
    <Column ref={containerRef} className="h-full">
      <Row
        className={cn(
          "h-[64px] px-4 gap-2 items-center border border-solid border-grey-300 border-t-transparent",
          containerWidth < 300 && "justify-center"
        )}
      >
        <Icon icon="Wisnio" color="purple" />
        {containerWidth >= 300 && (
          <p className="text-purple-500">{chatTitle || "WisGPT"}</p>
        )}
      </Row>
      {containerWidth >= 300 && (
        <Column
          className={cn(
            "flex-1 relative border-l border-solid border-grey-300 p-4 pb-0 overflow-hidden",
            bottomOffset && "pb-[84px]"
          )}
        >
          <MessagesList
            messages={chatMessages}
            renderItem={renderChatMessage}
            toolCallLoader={
              <ToolCallLoader
                toolCall={latestToolCall}
                isThinking={isThinking}
              />
            }
          />
          <ExamplePromptsSelect
            onSelect={handleSelectExamplePrompt}
            isHidden={currentFlowStatus != "idle"}
            defaultOpen={
              chatMessages.filter((m) => m.role === "user").length === 0
            }
            prompts={examplePrompts || []}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <ChatTextInput
              disabled={flowStatus != "idle"}
              {...register("message")}
              onIconClick={handleSubmit(onSubmit)}
              placeholder={
                flowStatus == "idle"
                  ? "Ask WisGPT by typing or by choosing a prompt"
                  : "Chat is not available at the moment"
              }
            />
          </form>
        </Column>
      )}
    </Column>
  );
};

export default GatewayChat;
